/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { StateObservable } from 'redux-observable';
import type { AxiosResponse } from 'axios';

import { resolveRestApi } from '../../router/app-router';
import { fetch } from '../../service_broker/fetch';
import { ACTION_SAVE } from '../../store/state/actions';
import { getSubmitData, processHeaders } from '../../store/state/epics/util';
import { logAndCatchError } from '../../utils/rxjs-utils';
import type { ErrorAction, State } from '../../types';

import type {
  ActionWithPayload,
  AsyncResponse,
  PerformAndSaveActionObject,
  PerformAndSaveActionPayload
} from './types';

export const executeSaveAssignmentRequest = (
  actionInPayload: PerformAndSaveActionPayload,
  state$: StateObservable<State>
): Observable<
  | (ActionWithPayload<PerformAndSaveActionObject> & {
      actionInPayload: {
        isProcessAction: boolean;
        containerItemID: string;
        RemoteSystemID?: string;
        context: string;
        caseID: string;
      };
      response: { data: { caseInfo: any } };
      headers: AxiosResponse['headers'];
    })
  | (ErrorAction & {
      actionInPayload: {
        isProcessAction: boolean;
        containerItemID: string;
        RemoteSystemID?: string;
        context: string;
        caseID: string;
      };
      response: { data: { caseInfo: any } };
      headers: AxiosResponse['headers'];
    })
> => {
  const { assignmentID, actionID, context } = actionInPayload;
  const { restApi, reqType } = resolveRestApi(ACTION_SAVE, { assignmentID, actionID }) as {
    restApi: string;
    reqType: string;
  };
  const { content } = getSubmitData(context);
  const reqHeaders = state$.value.data[context]?.caseInfo?.headers;

  return from(
    fetch(
      restApi,
      {
        method: reqType,
        body: content,
        headers: {
          'if-match': reqHeaders && reqHeaders.etag
        }
      },
      context
    )
  ).pipe(
    map((response): AsyncResponse => {
      return {
        response: response.data,
        actionInPayload,
        headers: processHeaders(response.headers)
      };
    }),
    logAndCatchError(': Error occurred during ajax call at saveAndCloseAssignment Epic at save action : ', context)
  );
};

export default { executeSaveAssignmentRequest };
